/**
 * Notification System Types
 *
 * This module defines the unified notification payload schema and type-safe
 * data structures for all notification types across the platform.
 */

import type { AnnouncedOfficeHours } from "../handbook-office-hours-calendar";

// ============================================================================
// Notification Types
// ============================================================================

export const NotificationTypes = {
  ORGANIZATION_INVITATION_CREATED: "ORGANIZATION_INVITATION_CREATED",
  ORGANIZATION_INVITATION_ACCEPTED: "ORGANIZATION_INVITATION_ACCEPTED",
  ORGANIZATION_INVITATION_REVOKED: "ORGANIZATION_INVITATION_REVOKED",
  INTERVIEW_INVITATION_SENT: "INTERVIEW_INVITATION_SENT",
  INTERVIEW_EARNINGS_POSTED: "INTERVIEW_EARNINGS_POSTED",
  BUDGET_THRESHOLD_REACHED: "BUDGET_THRESHOLD_REACHED",
  STUDY_APPROVAL_REQUESTED: "STUDY_APPROVAL_REQUESTED",
  STUDY_APPROVAL_DECIDED: "STUDY_APPROVAL_DECIDED",
  STUDY_STARTED: "STUDY_STARTED",
  STUDY_COMPLETED: "STUDY_COMPLETED",
  INTERVIEW_APPROVED: "INTERVIEW_APPROVED",
  INTERVIEW_AUTO_APPROVED: "INTERVIEW_AUTO_APPROVED",
  INTERVIEW_REJECTED: "INTERVIEW_REJECTED",
  PAYMENT_CLAWED_BACK: "PAYMENT_CLAWED_BACK",
  BALANCE_NEGATIVE: "BALANCE_NEGATIVE",
  // Panel engagement notifications
  INVITATION_REMINDER: "INVITATION_REMINDER",
  INVITATION_REOPENED: "INVITATION_REOPENED",
  POST_FIRST_INTERVIEW: "POST_FIRST_INTERVIEW",
  INVITATION_FEEDBACK_REQUEST: "INVITATION_FEEDBACK_REQUEST",
  POWER_USER_NPS: "POWER_USER_NPS",
  // Study health alerts
  STUDY_STUCK: "STUDY_STUCK",
  STUDY_FULFILLMENT_ALERT: "STUDY_FULFILLMENT_ALERT",
  // Support notifications
  SUPPORT_AUTO_RESPONSE: "SUPPORT_AUTO_RESPONSE",
  // System notifications
  WELCOME: "WELCOME",
  // Referral outreach
  REFERRAL_OUTREACH_REQUEST: "REFERRAL_OUTREACH_REQUEST",
  POST_COMPLETION_REFERRAL: "POST_COMPLETION_REFERRAL",
  // Multi-task studies
  NEXT_TASK_AVAILABLE: "NEXT_TASK_AVAILABLE",
  TASK_APPROVED: "TASK_APPROVED",
  // Lifecycle emails (cron + event-driven)
  WELCOME_NUDGE_FIRST: "WELCOME_NUDGE_FIRST",
  WELCOME_NUDGE_LAST: "WELCOME_NUDGE_LAST",
  FINISH_PROFILE_FIRST: "FINISH_PROFILE_FIRST",
  FINISH_PROFILE_LAST: "FINISH_PROFILE_LAST",
  ONBOARDING_CONFIRMED: "ONBOARDING_CONFIRMED",
  APPROVED_NOT_STARTED_FIRST: "APPROVED_NOT_STARTED_FIRST",
  APPROVED_NOT_STARTED_LAST: "APPROVED_NOT_STARTED_LAST",
  APPLIED_SCREENED_OUT: "APPLIED_SCREENED_OUT",
  APPLIED_NOT_SELECTED: "APPLIED_NOT_SELECTED",
  DORMANT_30_DAYS: "DORMANT_30_DAYS",
  DORMANT_60_DAYS: "DORMANT_60_DAYS",
  DORMANT_90_DAYS: "DORMANT_90_DAYS",
  COMPLETE_MATCH_PROFILE: "COMPLETE_MATCH_PROFILE",
  // Turnaround notifications (customer-facing)
  STUDY_ETA_SET: "STUDY_ETA_SET",
  STUDY_ETA_UPDATED: "STUDY_ETA_UPDATED",
  // Referrer alert: a new opportunity listing is available to refer
  OPPORTUNITY_LISTED: "OPPORTUNITY_LISTED",
  // First-opportunity onboarding emails (screening review wait + screen pass)
  FIRST_OPPORTUNITY_AWAITING_REVIEW: "FIRST_OPPORTUNITY_AWAITING_REVIEW",
  FIRST_OPPORTUNITY_SCREEN_PASSED: "FIRST_OPPORTUNITY_SCREEN_PASSED",
  // Screen-pass approval for a returning expert
  OPPORTUNITY_SCREEN_PASSED: "OPPORTUNITY_SCREEN_PASSED",
  // A screening step the applicant can start just became available to them
  SCREENING_STEP_READY: "SCREENING_STEP_READY",
  // Network connections: "your connection {name} fits study X" digest
  CONNECTION_MATCH: "CONNECTION_MATCH",
  // Feasibility: the researcher's study-creation quote is priced and ready to review
  FEASIBILITY_QUOTE_READY: "FEASIBILITY_QUOTE_READY",
  // Researcher bump: experts shared their calendars and are waiting to be booked
  STUDY_BOOKING_NEEDED: "STUDY_BOOKING_NEEDED",
  // Researcher digest: applicant and completed-work decisions waiting in the review queue
  RESEARCHER_REVIEW_DIGEST: "RESEARCHER_REVIEW_DIGEST",
  // Terac-booked scheduling: a slot was taken on the expert's calendar
  INTERVIEW_BOOKED: "INTERVIEW_BOOKED",
  // Terac-booked scheduling: the expert's nudge on the day of the call, and
  // again an hour before it. The stage lives in the payload, not in the type.
  INTERVIEW_REMINDER: "INTERVIEW_REMINDER",
  // Ad-hoc message an SPL sent to selected participants of one study
  STUDY_EMAIL_BLAST: "STUDY_EMAIL_BLAST",
  // The expert's copy of a contract they signed through an E_SIGNATURE task
  CONTRACT_SIGNED: "CONTRACT_SIGNED",
  OFFICE_HOURS_ANNOUNCED: "OFFICE_HOURS_ANNOUNCED",
  // An expert rejected off a project study they were on
  PROJECT_OFFBOARDED: "PROJECT_OFFBOARDED",
  // Everyone still on a project study when it closes
  PROJECT_CLOSED: "PROJECT_CLOSED",
} as const;

export type NotificationType =
  (typeof NotificationTypes)[keyof typeof NotificationTypes];

// ============================================================================
// Notification Priority
// ============================================================================

export const NotificationPriority = {
  LOW: "low",
  NORMAL: "normal",
  HIGH: "high",
  CRITICAL: "critical",
} as const;

export type NotificationPriorityType =
  (typeof NotificationPriority)[keyof typeof NotificationPriority];

// ============================================================================
// Notification Action Types
// ============================================================================

export const NotificationActionType = {
  NAVIGATE: "navigate",
  EXTERNAL: "external",
  DISMISS: "dismiss",
} as const;

export type NotificationActionTypeValue =
  (typeof NotificationActionType)[keyof typeof NotificationActionType];

/**
 * Defines the action to take when a notification is clicked/tapped
 */
export interface NotificationAction {
  /** Type of action to perform */
  type: NotificationActionTypeValue;
  /** Web path for navigate/external actions (e.g., "/acme/studies/123") */
  webPath?: string;
  /** iOS deep link for navigate/external actions (e.g., "terac://study/123") */
  iosDeepLink?: string;
}

// ============================================================================
// Typed Data Payloads per Notification Type
// ============================================================================

export interface OrganizationInvitationCreatedData {
  invitationId: string;
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  inviterName?: string;
  role?: string;
  action: NotificationAction;
}

export interface OrganizationInvitationAcceptedData {
  organizationId: string;
  organizationSlug: string;
  organizationName: string;
  acceptedByName: string;
  acceptedByEmail?: string;
  action: NotificationAction;
}

export interface OrganizationInvitationRevokedData {
  organizationName: string;
  action: NotificationAction;
}

/** What an invitation notification quotes, on every channel. */
export interface InvitationPayData {
  paymentAmount: number;
  payRateFrequency?: string | null;
}

export interface InterviewInvitationSentData extends InvitationPayData {
  studyId: string;
  studyName: string;
  organizationName: string;
  interviewLink: string;
  emailContent?: string;
  emailSubject?: string;
  expiresAt?: string;
  senderId?: string;
  senderName?: string;
  action: NotificationAction;
}

export interface InterviewEarningsPostedData {
  interviewId: string;
  studyId: string;
  studyName: string;
  amount: number;
  bonusAmount?: number;
  totalEarnings?: number;
  action: NotificationAction;
}

export interface BudgetThresholdReachedData {
  studyId: string;
  studyName: string;
  organizationId: string;
  organizationSlug: string;
  projectSlug: string;
  thresholdPercent: number; // 50, 80, or 100
  currentSpend: number;
  totalBudget: number;
  action: NotificationAction;
}

export interface StudyApprovalRequestedData {
  studyId: string;
  studyName: string;
  organizationId: string;
  organizationSlug: string;
  projectSlug: string;
  requesterId: string;
  requesterName: string;
  action: NotificationAction;
}

export interface StudyApprovalDecidedData {
  studyId: string;
  studyName: string;
  organizationId: string;
  organizationSlug: string;
  projectSlug: string;
  approved: boolean;
  decidedByName?: string;
  reason?: string;
  action: NotificationAction;
}

export interface StudyStartedData {
  studyId: string;
  studyName: string;
  organizationId: string;
  organizationSlug: string;
  projectSlug: string;
  startedAt: Date;
  action: NotificationAction;
}

export interface StudyCompletedData {
  studyId: string;
  studyName: string;
  opportunitiesCount: number;
  recipientName?: string;
}

export interface InterviewApprovedData {
  interviewId: string;
  studyId: string;
  studyName: string;
  paymentAmount: number;
  bonusAmount?: number;
  reviewerNote?: string;
  action: NotificationAction;
}

export interface InterviewAutoApprovedData {
  interviewId: string;
  studyId: string;
  studyName: string;
  paymentAmount: number;
  autoApprovalReason?: string;
  action: NotificationAction;
}

export interface InterviewRejectedData {
  interviewId: string;
  studyId: string;
  studyName: string;
  rejectionCategory: string;
  rejectionReason: string;
  clawbackAmount?: number;
  appealDeadline?: string;
  action: NotificationAction;
}

/** Sent when a strike takes back a one time submission's pay. */
export interface PaymentClawedBackData {
  submissionId: string;
  studyId: string;
  studyName?: string;
  clawbackAmount: number;
  newBalance: number;
  reason: string;
}

export interface BalanceNegativeData {
  currentBalance: number;
  outstandingAmount: number;
  reason?: string;
  action: NotificationAction;
}

// Panel engagement notification data
export interface InvitationReminderData extends InvitationPayData {
  invitationId: string;
  studyId?: string | null;
  studyName: string;
  duration?: number;
  invitationLink: string;
  expiresAt?: string;
}

export interface InvitationReopenedData extends InvitationPayData {
  invitationId: string;
  studyId?: string | null;
  studyName: string;
  duration?: number;
  invitationLink: string;
}

export interface PostFirstInterviewData {
  studyId?: string | null;
  studyName: string;
  matchingStudiesCount?: number;
  dashboardLink: string;
}

export interface InvitationFeedbackRequestData extends InvitationPayData {
  invitationId: string;
  studyId?: string | null;
  studyName: string;
  feedbackLink: string;
}

export interface PowerUserNpsData {
  interviewCount: number;
  totalEarned: number;
  npsLink: string;
}

export interface StudyStuckData {
  studyId: string;
  studyName: string;
  organizationName: string;
  hoursActive: number;
  totalInterviews: number;
  screenedOutCount: number;
  screenOutRate: number;
  studyLink: string;
}

export interface StudyFulfillmentAlertData {
  studyId: string;
  studyName: string;
  organizationName: string;
  targetResponses: number;
  approvedResponses: number;
  fulfillmentPercent: number;
  hoursActive: number;
  alertReason: "approaching_target" | "over_target" | "slow_fulfillment";
  studyLink: string;
}

export interface SupportAutoResponseData {
  firstName: string;
  originalSubject: string;
  resendEntityRefId: string;
}

export interface WelcomeData {
  chatLink: string;
}

export interface NextTaskAvailableData {
  submissionId: string;
  studyId: string;
  studyName: string;
  invitationId: string;
  nextTaskSequence: number;
  nextTaskTitle: string | null;
  nextTaskDescription: string | null;
  nextTaskType: "INTERVIEW" | "ACTIVITY" | "SURVEY" | "FILE_UPLOAD";
  offeredAmount: number | null;
  continueLink: string;
  action: NotificationAction;
}

export interface TaskApprovedData {
  submissionId: string;
  studyId: string;
  studyName: string;
  taskSequence: number;
  taskTitle: string | null;
  taskType: "INTERVIEW" | "ACTIVITY" | "SURVEY" | "FILE_UPLOAD";
  totalTasks: number;
  completedTasks: number;
  nextTaskTitle: string | null;
  nextTaskType: string | null;
  hasDelay: boolean;
  /**
   * The study pays HOURLY or PER_TASK. Its last task leaves the submission in
   * progress with the expert on the project, so that email does not promise a
   * finished submission or a payout. Absent on notifications written before it.
   */
  isProjectStudy?: boolean;
  continueLink: string;
  action: NotificationAction;
}

export interface ProjectOffboardedData {
  submissionId: string;
  studyId: string;
  studyName: string;
  /** When the submission was rejected, ISO 8601. */
  offboardedAtUtc: string;
  /** The expert's IANA zone, so the day they read is their own. */
  expertTimezone: string | null;
  action: NotificationAction;
}

export interface ProjectClosedData {
  submissionId: string;
  studyId: string;
  studyName: string;
  /** When the study stopped or completed, ISO 8601. */
  closedAtUtc: string;
  /** The expert's IANA zone, so the day they read is their own. */
  expertTimezone: string | null;
  /** Picks the opening line: thanks for the work, or for joining. */
  projectStage: "ONBOARDING" | "ONBOARDED";
  action: NotificationAction;
}

export interface ContractSignedData {
  submissionId: string;
  submissionTaskId: string;
  studyId: string;
  studyName: string;
  documentTitle: string;
  /** When BoldSign reported the signature, ISO 8601. */
  signedAtUtc: string;
  /** The signer's IANA zone, so the date on their copy is the date they signed. */
  signerTimezone: string | null;
  downloadLink: string;
  action: NotificationAction;
}

export interface PostCompletionReferralData {
  studyId: string;
  studyName: string;
  referralLink: string;
  commissionPercent: number;
  referralEarnings?: string;
}

export interface ReferralOutreachRequestData {
  studyId: string;
  studyName: string;
  studyDescription?: string;
  referralTitle?: string;
  referralShortDescription?: string;
  referralPriceFormatted?: string;
  referralLink: string;
  opportunityListingLink?: string;
  commissionPercent: number;
  action: NotificationAction;
}

export interface StudyTurnaroundSetData {
  studyId: string;
  studyName: string;
  organizationId: string;
  organizationSlug: string;
  projectSlug: string;
  /** ETA pre-formatted as "Month D, YYYY" (e.g. "May 12, 2026"). No reason included; internal-only. */
  eta: string;
  action: NotificationAction;
}

export interface StudyTurnaroundUpdatedData {
  studyId: string;
  studyName: string;
  organizationId: string;
  organizationSlug: string;
  projectSlug: string;
  /** ETA pre-formatted as "Month D, YYYY" (e.g. "May 12, 2026"). No reason included; internal-only. */
  eta: string;
  action: NotificationAction;
}

export interface FeasibilityQuoteReadyData {
  /**
   * Set only when the request came from the study builder. A request that
   * arrived by email, Slack, MCP or the API is priced before any study exists,
   * so it carries the organization fields alone.
   */
  studyId?: string;
  studyName?: string;
  projectSlug?: string;
  organizationId: string;
  organizationSlug: string;
  /**
   * Full URL to wherever this quote is acted on: the study-creation review step
   * when there is a draft, otherwise the org's feasibility requests page.
   */
  reviewLink: string;
  action: NotificationAction;
}

export interface StudyBookingNeededData {
  studyId: string;
  studyName: string;
  organizationId: string;
  organizationSlug: string;
  projectSlug: string;
  /** Number of experts on this study sitting in AWAITING_BOOKING past the age gate. */
  waitingCount: number;
  /** Absolute deep link to the study's submissions page (drives the plain-text email). */
  submissionsLink: string;
  action: NotificationAction;
}

export type ResearcherReviewDigestStage =
  | "NEW"
  | "REMINDER"
  | "ESCALATION"
  | "WEEKLY";

export interface ResearcherReviewDigestStudy {
  studyId: string;
  studyName: string;
  applicantReviewCount: number;
  workReviewCount: number;
}

export interface ResearcherReviewDigestData {
  stage: ResearcherReviewDigestStage;
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  totalCount: number;
  newCount: number;
  applicantReviewCount: number;
  workReviewCount: number;
  studies: ResearcherReviewDigestStudy[];
  moreStudiesCount: number;
  actionableKeys: string[];
  latestActionableAt: string;
  reviewUrl: string;
  action: NotificationAction;
}

/**
 * A Terac-booked interview that was just taken on the expert's own calendar.
 * Both sides get a row; `audience` is what keeps the two copies apart.
 */
export interface InterviewBookedData {
  audience: "EXPERT" | "RESEARCHER";
  studyId: string;
  studyName: string;
  organizationId: string;
  submissionTaskId: string;
  /** Slot start as a UTC instant; every rendered time derives from it. */
  slotStartUtc: string;
  /**
   * The IANA zone the availability read used, copied off the row. Rendering the
   * expert's time from their current profile instead would let a later profile
   * edit re-time a call that is already on their calendar.
   */
  slotTimezone: string;
  /** Google Meet link, when Google minted a conference for the event. */
  joinUrl?: string;
  /** RESEARCHER only: absolute deep link to the study's submissions page. */
  submissionsLink?: string;
  action: NotificationAction;
}

/**
 * One of the two expert-facing nudges ahead of a Terac-booked call. Expert-only:
 * the researcher is a Google attendee with sendUpdates "all" and already gets
 * Google's own reminders.
 */
export interface InterviewReminderData {
  audience: "EXPERT";
  /**
   * T_MINUS_24H is the day-ahead nudge, DAY_OF the morning one for an afternoon
   * call, T_MINUS_1H the get-ready tap. The email channel branches on this rather
   * than on the title, so a copy edit cannot silently re-route a template.
   */
  stage: "T_MINUS_24H" | "DAY_OF" | "T_MINUS_1H";
  studyId: string;
  studyName: string;
  organizationId: string;
  submissionTaskId: string;
  /** Slot start as a UTC instant; every rendered time derives from it. */
  slotStartUtc: string;
  /**
   * Slot end as a UTC instant. Only ever used to state a duration, so a row
   * without one costs a line in the email rather than the email.
   */
  slotEndUtc?: string;
  /**
   * The expert's own zone, resolved by the sweep. Never the row's `slotTimezone`
   * on its own: that column holds the hosting team's zone on a
   * researcher-hosted booking, and every clock here is addressed to the expert.
   *
   * Optional because every stage still fires without one, dropping the clock
   * from the copy rather than the nudge.
   */
  expertTimezone?: string;
  /** Google Meet link, when Google minted a conference for the event. */
  joinUrl?: string;
  action: NotificationAction;
}

export interface OpportunityListedData {
  /** Absent on legacy rows; what the payload union discriminates on. */
  variant?: undefined;
  /** Public token of the OpportunityListing, used for the dashboard deep link */
  listingToken: string;
  /** Human-readable opportunity title shown in the email subject/body */
  opportunityTitle: string;
  /** Referrals-dashboard deep link, pre-opened on this opportunity */
  dashboardLink: string;
  action: NotificationAction;
}

export interface OpportunityDigestListing {
  /** Required: the sent-set is read back out of this, so a row without one re-sends forever. */
  id: string;
  token: string;
  title: string;
  /** Per listing, not a platform default: commission is set per study. */
  commissionPercent: number;
  estimatedEarnings?: string;
}

/** One row per recipient per local send slot, replacing the per-listing blast. */
export interface OpportunityDigestData {
  variant: "DIGEST";
  /** Local send hour this row was produced for (9 or 21). */
  slot: number;
  listings: OpportunityDigestListing[];
  /** Live unsent listings beyond the ones in `listings`. */
  moreCount: number;
  browseAllUrl: string;
  action: NotificationAction;
}

/**
 * Build the welcome notification chatLink URL.
 * UTM tagging happens in packages/api/src/services/notifications/channels/email.ts.
 */
export function buildWelcomeChatLink(baseUrl: string): string {
  return `${baseUrl}/me`;
}

// ============================================================================
// Notification Data Map
// ============================================================================

/**
 * Maps each notification type to its strongly-typed data payload
 */
export interface NotificationDataMap {
  ORGANIZATION_INVITATION_CREATED: OrganizationInvitationCreatedData;
  ORGANIZATION_INVITATION_ACCEPTED: OrganizationInvitationAcceptedData;
  ORGANIZATION_INVITATION_REVOKED: OrganizationInvitationRevokedData;
  INTERVIEW_INVITATION_SENT: InterviewInvitationSentData;
  INTERVIEW_EARNINGS_POSTED: InterviewEarningsPostedData;
  BUDGET_THRESHOLD_REACHED: BudgetThresholdReachedData;
  STUDY_APPROVAL_REQUESTED: StudyApprovalRequestedData;
  STUDY_APPROVAL_DECIDED: StudyApprovalDecidedData;
  STUDY_STARTED: StudyStartedData;
  STUDY_COMPLETED: StudyCompletedData;
  INTERVIEW_APPROVED: InterviewApprovedData;
  INTERVIEW_AUTO_APPROVED: InterviewAutoApprovedData;
  INTERVIEW_REJECTED: InterviewRejectedData;
  PAYMENT_CLAWED_BACK: PaymentClawedBackData;
  BALANCE_NEGATIVE: BalanceNegativeData;
  // Panel engagement
  INVITATION_REMINDER: InvitationReminderData;
  INVITATION_REOPENED: InvitationReopenedData;
  POST_FIRST_INTERVIEW: PostFirstInterviewData;
  INVITATION_FEEDBACK_REQUEST: InvitationFeedbackRequestData;
  POWER_USER_NPS: PowerUserNpsData;
  // Study health
  STUDY_STUCK: StudyStuckData;
  STUDY_FULFILLMENT_ALERT: StudyFulfillmentAlertData;
  // Support
  SUPPORT_AUTO_RESPONSE: SupportAutoResponseData;
  // System
  WELCOME: WelcomeData;
  // Referral outreach
  REFERRAL_OUTREACH_REQUEST: ReferralOutreachRequestData;
  POST_COMPLETION_REFERRAL: PostCompletionReferralData;
  // Multi-task studies
  NEXT_TASK_AVAILABLE: NextTaskAvailableData;
  TASK_APPROVED: TaskApprovedData;
  // Turnaround notifications (customer-facing)
  STUDY_ETA_SET: StudyTurnaroundSetData;
  STUDY_ETA_UPDATED: StudyTurnaroundUpdatedData;
  // Referrer alert
  OPPORTUNITY_LISTED: OpportunityListedData | OpportunityDigestData;
  // Lifecycle emails use a generic payload; sender cron/events fill in the fields
  // relevant to each email (e.g. studyId for approved-not-started).
  WELCOME_NUDGE_FIRST: LifecycleEmailData;
  WELCOME_NUDGE_LAST: LifecycleEmailData;
  FINISH_PROFILE_FIRST: LifecycleEmailData;
  FINISH_PROFILE_LAST: LifecycleEmailData;
  ONBOARDING_CONFIRMED: LifecycleEmailData;
  APPROVED_NOT_STARTED_FIRST: LifecycleEmailData;
  APPROVED_NOT_STARTED_LAST: LifecycleEmailData;
  APPLIED_SCREENED_OUT: LifecycleEmailData;
  APPLIED_NOT_SELECTED: LifecycleEmailData;
  DORMANT_30_DAYS: LifecycleEmailData;
  DORMANT_60_DAYS: LifecycleEmailData;
  DORMANT_90_DAYS: LifecycleEmailData;
  COMPLETE_MATCH_PROFILE: LifecycleEmailData;
  FIRST_OPPORTUNITY_AWAITING_REVIEW: LifecycleEmailData;
  FIRST_OPPORTUNITY_SCREEN_PASSED: LifecycleEmailData;
  OPPORTUNITY_SCREEN_PASSED: LifecycleEmailData;
  SCREENING_STEP_READY: LifecycleEmailData;
  // Network connections: "your connection fits study X"
  CONNECTION_MATCH: ConnectionMatchData;
  // Feasibility: study-creation quote priced and ready to review
  FEASIBILITY_QUOTE_READY: FeasibilityQuoteReadyData;
  // Researcher bump: experts shared their calendars and are waiting to be booked
  STUDY_BOOKING_NEEDED: StudyBookingNeededData;
  // Researcher digest: applicant and completed-work review queues
  RESEARCHER_REVIEW_DIGEST: ResearcherReviewDigestData;
  // Terac-booked scheduling: a slot was taken on the expert's calendar
  INTERVIEW_BOOKED: InterviewBookedData;
  // Terac-booked scheduling: the expert's day-of and hour-before nudges
  INTERVIEW_REMINDER: InterviewReminderData;
  // Ad-hoc SPL message; ids only, the copy lives on the StudyEmailBlast row
  STUDY_EMAIL_BLAST: StudyEmailBlastData;
  // The expert's copy of a contract they signed on an E_SIGNATURE task
  CONTRACT_SIGNED: ContractSignedData;
  // A study's office hours as a calendar invitation, and any change to them
  OFFICE_HOURS_ANNOUNCED: OfficeHoursAnnouncedData;
  // An expert rejected off a project study they were on
  PROJECT_OFFBOARDED: ProjectOffboardedData;
  // Everyone still on a project study when it closes
  PROJECT_CLOSED: ProjectClosedData;
}

/**
 * The record of what one expert was told about one study's office hours.
 *
 * Validated at runtime by `officeHoursAnnouncementDataSchema` in the API, on
 * both the write and every read-back; this interface is the shape that schema
 * produces, for the typed data map.
 */
export interface OfficeHoursAnnouncedData {
  studyId: string;
  studyName: string;
  organizationId: string | null;
  /** The reader's first series anchor, ISO 8601; fixes the recurrence start. */
  seriesAnchor: string;
  prior: AnnouncedOfficeHours | null;
  announcement: AnnouncedOfficeHours;
  action: NotificationAction;
}

/**
 * Study email blast payload. Deliberately ids only: the subject and body live
 * on the StudyEmailBlast row so one copy backs every recipient, and the email
 * channel loads them from there.
 */
export interface StudyEmailBlastData {
  blastId: string;
  studyId: string;
  submissionId: string;
  studyName?: string;
  organizationId?: string;
  projectId?: string;
}

/**
 * Generic data shape for lifecycle emails. Downstream PRs can extend
 * specific fields as needed; keeping this loose avoids churn while the
 * content spec is still being tuned.
 */
export interface LifecycleEmailData {
  recipientName?: string;
  studyId?: string;
  studyName?: string;
  primaryUrl?: string;
  action?: NotificationAction;
}

/** A matched person rendered as a mini-profile in the digest email. */
export interface ConnectionMatchPerson {
  connectionProfileId: string;
  name: string;
  position?: string | null;
  company?: string | null;
  /** S3 key; the email channel resolves it to a public CloudFront URL. */
  photoS3Key?: string | null;
}

/**
 * Connection-match notification payload. The `variant` discriminator is what
 * keeps the two directions apart at the email layer:
 * - DIGEST (Direction A, study launch): renders + sends the digest email AND
 *   shows in-app.
 * - AGGREGATE (Direction B, owner upload): in-app only; the email channel
 *   returns without sending for this variant.
 */
/** One study's block inside a BATCH row: the study, and who in the network fits it. */
export interface ConnectionMatchStudy {
  /** Required: the sent-set is read back out of this, so a block without one re-sends forever. */
  studyId: string;
  studyName: string;
  commissionPercent: number;
  /** Every match on this study, including the ones beyond `matches`. */
  matchCount: number;
  matches: ConnectionMatchPerson[];
  /** Matches beyond the ones in `matches` (drives the "and N more" line). */
  moreCount: number;
}

export interface ConnectionMatchData {
  variant: "DIGEST" | "AGGREGATE" | "BATCH";
  // BATCH: one row per recipient per local send slot, covering every study the
  // recipient's network fits.
  studies?: ConnectionMatchStudy[];
  /** BATCH only: local send hour this row was produced for (9 or 21). */
  slot?: number;
  // DIGEST (legacy, one row per study per launch). Nothing writes this shape any
  // more; it stays because the rows are the sent-set the batch reads back.
  studyId?: string;
  studyName?: string;
  matchCount?: number;
  commissionPercent?: number;
  matches?: ConnectionMatchPerson[];
  moreCount?: number;
  primaryUrl?: string;
  action?: NotificationAction;
}

// ============================================================================
// Unified Notification Payload
// ============================================================================

/**
 * Notification content structure
 */
export interface NotificationContent {
  /** Notification title */
  title: string;
  /** Plain text body for SMS/push (character-limited) */
  body: string;
  /** Rich markdown body for in-app/email display */
  richBody?: string;
}

/**
 * Unified notification payload that works across all channels
 */
export interface NotificationPayload<T extends NotificationType> {
  /** Notification type */
  type: T;
  /** Priority level */
  priority: NotificationPriorityType;
  /** Content for various channels */
  content: NotificationContent;
  /** Action to perform on click/tap */
  action: NotificationAction;
  /** Type-safe data payload */
  data: NotificationDataMap[T];
  /** Optional metadata */
  metadata?: {
    /** When the notification was created */
    createdAt?: string;
    /** When the notification expires */
    expiresAt?: string;
    /** Source that triggered the notification */
    source?: string;
  };
}

/**
 * Generic notification payload for cases where type is not known at compile time
 */
export type AnyNotificationPayload = NotificationPayload<NotificationType>;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Creates a notification action for navigation
 */
export function createNavigateAction(
  webPath: string,
  iosDeepLink?: string,
): NotificationAction {
  return {
    type: NotificationActionType.NAVIGATE,
    webPath,
    iosDeepLink,
  };
}

/**
 * Creates a notification action for external links
 */
export function createExternalAction(
  webPath: string,
  iosDeepLink?: string,
): NotificationAction {
  return {
    type: NotificationActionType.EXTERNAL,
    webPath,
    iosDeepLink,
  };
}

/**
 * Creates a dismiss-only action (no navigation)
 */
export function createDismissAction(): NotificationAction {
  return {
    type: NotificationActionType.DISMISS,
  };
}
